import { useState, type CSSProperties } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import clsx from 'clsx';
import styles from './DataTable.module.css';

export type ColMeta = {
  align?: 'left' | 'right' | 'center';
  /** CSS width (e.g. `1%` with `nowrap` shrinks column to content in auto tables). */
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  nowrap?: boolean;
  /** Allow long cell text to wrap within maxWidth. */
  wrap?: boolean;
};

function cellStyle(meta: ColMeta | undefined): CSSProperties {
  return {
    textAlign: meta?.align ?? 'left',
    width: meta?.width,
    minWidth: meta?.minWidth,
    maxWidth: meta?.maxWidth,
    whiteSpace: meta?.nowrap ? 'nowrap' : undefined,
    wordBreak: meta?.wrap ? 'break-word' : undefined,
    overflowWrap: meta?.wrap ? 'anywhere' : undefined,
  };
}

/** Pass from `getRowClassName` when a row is inactive / deactivated. */
export const dataTableRowClass = {
  inactive: styles.trInactive,
} as const;

export function DataTable<T>({
  data,
  columns,
  onRowClick,
  enableSorting = false,
  getRowClassName,
  layout = 'full',
}: {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  /** Entire row is clickable (e.g. navigate). Use `stopPropagation` on buttons/links inside cells that should not activate this. */
  onRowClick?: (row: T) => void;
  /** Column headers toggle sort (asc → desc → none). */
  enableSorting?: boolean;
  getRowClassName?: (row: T) => string | undefined;
  /** `content` sizes columns to cell content instead of stretching to full width. */
  layout?: 'full' | 'content';
}) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(enableSorting
      ? {
          state: { sorting },
          onSortingChange: setSorting,
          getSortedRowModel: getSortedRowModel(),
        }
      : {}),
  });

  const isContentLayout = layout === 'content';

  return (
    <div className={clsx(styles.wrap, isContentLayout && styles.wrapContent)}>
      <table className={clsx(styles.table, isContentLayout && styles.tableContent)}>
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => {
                const meta = h.column.columnDef.meta as ColMeta | undefined;
                const align = meta?.align ?? 'left';
                const canSort = enableSorting && h.column.getCanSort();
                const sorted = enableSorting ? h.column.getIsSorted() : false;
                return (
                  <th
                    key={h.id}
                    className={clsx(styles.th, enableSorting && canSort && styles.thSortable)}
                    style={cellStyle({ align, ...meta })}
                    aria-sort={
                      sorted === 'asc'
                        ? 'ascending'
                        : sorted === 'desc'
                          ? 'descending'
                          : canSort
                            ? 'none'
                            : undefined
                    }
                  >
                    {h.isPlaceholder ? null : canSort ? (
                      <button
                        type="button"
                        className={clsx(styles.sortBtn, align === 'right' && styles.sortBtnRight)}
                        onClick={h.column.getToggleSortingHandler()}
                      >
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        <span className={styles.sortGlyph} aria-hidden>
                          {sorted === 'asc' ? ' ▲' : sorted === 'desc' ? ' ▼' : ''}
                        </span>
                      </button>
                    ) : (
                      flexRender(h.column.columnDef.header, h.getContext())
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={clsx(
                styles.tr,
                onRowClick && styles.trClickable,
                getRowClassName?.(row.original)
              )}
              onClick={onRowClick ? () => onRowClick(row.original) : undefined}
            >
              {row.getVisibleCells().map((cell) => {
                const meta = cell.column.columnDef.meta as ColMeta | undefined;
                return (
                  <td key={cell.id} className={styles.td} style={cellStyle(meta)}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
